import { client } from ".";

/**
 * Partner-side billing helpers. Mirrors a subset of mobile-app's
 * lib/axios/calls.js — the partner-app only ever joins an existing
 * billing session (the user kicks it off), so we don't need
 * `startBillingSession` here.
 *
 * These endpoints are now isUserOrAstrologer-gated on the server
 * (B11/B15/B16 in J4). The astrologer's bearer token is attached by
 * the shared `client` axios instance; the server's assertCallParty /
 * assertMeetingParty helpers reject any astrologer who isn't a party
 * on the underlying call/meeting. ViewerScope is also derived from
 * the token side now — the old ?scope=partner query param is ignored
 * server-side, but kept on outgoing requests for back-compat in case
 * an older server build is still running.
 *
 * For scheduled meetings, channelName === meeting._id. For instant
 * calls (if the astrologer ever needs to use these for instant),
 * channelName === Call.channelName. The server's resolveBillingChannel
 * branches on which collection matches.
 */

/**
 * Astrologer-side heartbeat. Server uses this to detect the partner
 * side disconnecting — heartbeatMissedThresholdSeconds=30 + grace=30
 * = ~60s before the session is force-ended with attribution=astrologer.
 *
 * Note: the prior comment here claimed this triggered a user-side
 * auto-refund. That auto-refund was removed (see the J4 comment in
 * server/src/controllers/common/call.controller.js#endBilling) because
 * Agora can't distinguish a voluntary hangup from a network drop, so
 * "astrologer ended" was getting refunded even on honest hangups.
 * Attribution is still recorded on the BillingSession for audit;
 * actual refunds now flow through the admin-only refundService.
 *
 * Send every 10s while in-call. Best-effort; the next heartbeat
 * masks transient failures.
 */
export const sendAstrologerHeartbeat = async (channelName) => {
  try {
    await client.post(`/api/calls/channel/${channelName}/heartbeat`, {
      participant: "astrologer",
    });
  } catch (err) {
    // Heartbeats are best-effort. The watchdog tolerates 30s of misses
    // before flagging the side as disconnected.
    // eslint-disable-next-line no-console
    console.debug("[heartbeat-astro] failed:", err?.message);
  }
};

/**
 * End the billing session from the astrologer side. Mirrors the
 * mobile helper — fires the billing-end path which on the server
 * also flips Meeting → completed via the meetingState service
 * (no need for a separate updateMeetingState call).
 *
 * attribution: 'astrologer' here means "the astrologer initiated the
 * end" (clean hangup), not "the astrologer dropped". The refund
 * service only triggers refunds for astrologer-dropped + matching
 * endReason; voluntary hangups stay 'astrologer_ended' with no refund.
 */
export const endBillingSessionAstrologer = async (
  channelName,
  endReason,
  attribution
) => {
  try {
    const { data } = await client.post(
      `/api/calls/channel/${channelName}/billing/end`,
      {
        endReason: endReason || "astrologer_ended",
        attribution: attribution || "astrologer",
      }
    );
    return data.data;
  } catch (err) {
    // 404 means no session (legacy meetings before D1, or a meeting the
    // user never started billing on). Don't log noise; just return null.
    if (err?.response?.status !== 404) {
      // eslint-disable-next-line no-console
      console.warn("[endBilling-astro] failed:", err?.message);
    }
    return null;
  }
};

/**
 * Partner-scoped billing status fetch. Server omits the user's live
 * wallet snapshot when scope=partner (default-deny on user-private
 * fields). Used by the in-call screen to surface a running "earned
 * so far" tile to the astrologer.
 *
 * Returns null on any error so the caller can render a fallback
 * without exception handling at the call site.
 */
export const getBillingStatusPartner = async (channelName) => {
  try {
    const { data } = await client.get(
      `/api/calls/channel/${channelName}/billing/status`,
      { params: { scope: "partner" } }
    );
    return data?.data || null;
  } catch (err) {
    if (err?.response?.status !== 404) {
      // eslint-disable-next-line no-console
      console.debug("[billing-status-partner] failed:", err?.message);
    }
    return null;
  }
};

/**
 * Transition a scheduled meeting to completed/cancelled. The server's
 * endBilling path already flips Meeting status on the billing side,
 * so this is mostly a belt-and-braces call for flows where billing
 * wasn't started (zero-duration leave-before-join, or legacy meetings).
 *
 * Best-effort: swallows 400s (already-terminal meetings) and logs
 * network failures without throwing.
 */
export const updateMeetingState = async (meetingId, newState, duration) => {
  try {
    const { data } = await client.put(`/api/meeting/update-state`, {
      meetingId,
      newState,
      ...(duration != null ? { duration } : {}),
    });
    return data?.data || null;
  } catch (err) {
    if (err?.response?.status !== 400) {
      // eslint-disable-next-line no-console
      console.warn("[updateMeetingState-astro] failed:", err?.message);
    }
    return null;
  }
};
